import React, { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { singleSelectComponents } from '../../components/CustomDashboard/multiSelectComponents';
import { useCurrentUser } from '../../contexts/CurrentUser';
import styles from './ArtifactsPage.module.scss';
import '../../components/CustomDashboard/wf-select-portal.scss';

// DEMO ONLY (gated by the `show_artifact_sharing` workspace flag): everything here is
// mocked — the people, the roles and the notification never leave the browser. Wire
// to a real API before showing it outside demos.

export type AccessRole = 'viewer' | 'editor';

export interface DemoPerson {
  id: string;
  name: string;
  email: string;
  title: string;
  color: string;
}

export interface AccessGrant {
  person: DemoPerson;
  role: AccessRole;
}

export interface DemoAccessState {
  grants: AccessGrant[];
  notify: boolean;
  message: string;
}

export const emptyDemoAccess = (): DemoAccessState => ({
  grants: [],
  notify: false,
  message: '',
});

const MESSAGE_MAX_LENGTH = 500;

const DEMO_PEOPLE: DemoPerson[] = [
  {
    id: 'p1',
    name: 'Kateryna Bondar',
    email: 'k.bondar@osavul.team',
    title: 'Head of Analytics',
    color: 'var(--color0)',
  },
  {
    id: 'p2',
    name: 'Marcus Feld',
    email: 'm.feld@osavul.team',
    title: 'OSINT Analyst',
    color: 'var(--color3)',
  },
  {
    id: 'p3',
    name: 'Yuliia Tkachenko',
    email: 'y.tkachenko@osavul.team',
    title: 'Senior Researcher',
    color: 'var(--pink-primary)',
  },
  {
    id: 'p4',
    name: 'Daniel Okafor',
    email: 'd.okafor@osavul.team',
    title: 'Communications Lead',
    color: 'var(--color1)',
  },
  {
    id: 'p5',
    name: 'Sofia Marchetti',
    email: 's.marchetti@osavul.team',
    title: 'Policy Advisor',
    color: 'var(--cyan-medium)',
  },
  {
    id: 'p6',
    name: 'Oleksii Hrytsenko',
    email: 'o.hrytsenko@osavul.team',
    title: 'Data Engineer',
    color: 'var(--info-medium)',
  },
  {
    id: 'p7',
    name: 'Hanna Weiss',
    email: 'h.weiss@osavul.team',
    title: 'Program Manager',
    color: 'var(--warning-medium)',
  },
  {
    id: 'p8',
    name: 'Tomasz Nowak',
    email: 't.nowak@osavul.team',
    title: 'Threat Intelligence',
    color: 'var(--purple-primary)',
  },
];

// The menu portals to document.body, so it has to clear the .modal overlay (z-index 9999)
const selectPortalProps: Record<string, any> = {
  menuPortalTarget: document.body,
  menuPosition: 'fixed' as const,
  styles: { menuPortal: (base: object) => ({ ...base, zIndex: 10000 }) },
};

// the role control is narrow, but its menu carries a hint line per option
const roleSelectPortalProps: Record<string, any> = {
  ...selectPortalProps,
  styles: {
    ...selectPortalProps.styles,
    menu: (base: object) => ({ ...base, minWidth: '15rem' }),
  },
};

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

export function mockEmail(name: string): string {
  const slug = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/\s+/)
    .map((part) => part.replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)
    .join('.');
  return `${slug || 'you'}@osavul.team`;
}

export function buildDefaultMessage(
  bundleTitle: string,
  workspaceName: string,
  t: (key: string, options?: any) => string,
): string {
  return t(
    'Hi! I have shared the "{{title}}" bundle with you in {{workspace}}. Take a look when you have a minute.',
    {
      title: bundleTitle.trim() || t('Untitled bundle'),
      workspace: workspaceName,
    },
  );
}

function Avatar({
  name,
  color,
  className,
}: {
  name: string;
  color: string;
  className?: string;
}) {
  return (
    <span
      className={[styles.avatar, className].filter(Boolean).join(' ')}
      style={{ background: color }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

interface PersonOptionShape {
  value: string;
  label: string;
  person: DemoPerson;
}

const PersonOption = ({ isFocused, innerProps, innerRef, data }: any) => {
  const { person } = data as PersonOptionShape;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={[
        'wf-single-option',
        isFocused && 'wf-single-option--focused',
        styles.personOption,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Avatar name={person.name} color={person.color} />
      <span className={styles.personText}>
        <span className={styles.personName}>{person.name}</span>
        <span className={styles.personMeta}>
          {person.title} · {person.email}
        </span>
      </span>
    </div>
  );
};

const personSelectComponents = {
  Option: PersonOption,
  DropdownIndicator: () => null,
  IndicatorSeparator: () => null,
};

interface DemoAccessSectionProps {
  state: DemoAccessState;
  onChange: (next: DemoAccessState) => void;
  bundleTitle: string;
  disabled?: boolean;
}

export function DemoAccessSection({
  state,
  onChange,
  bundleTitle,
  disabled,
}: DemoAccessSectionProps) {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const ids = useId();

  const workspaceName = currentUser?.workspace?.name || t('Your workspace');
  const ownerName =
    [currentUser?.first_name, currentUser?.last_name]
      .filter(Boolean)
      .join(' ') || t('You');
  // demo screens get recorded: never show the presenter's real address, fake one from the name
  const ownerEmail = mockEmail(ownerName);

  const roleOptions = useMemo(
    () => [
      {
        value: 'viewer' as AccessRole,
        label: t('Viewer'),
        hint: t('Can view and download'),
      },
      {
        value: 'editor' as AccessRole,
        label: t('Editor'),
        hint: t('Can edit and republish'),
      },
    ],
    [t],
  );

  const grantedIds = new Set(state.grants.map((grant) => grant.person.id));
  const personOptions: PersonOptionShape[] = DEMO_PEOPLE.filter(
    (person) => !grantedIds.has(person.id),
  ).map((person) => ({
    value: person.id,
    label: `${person.name} ${person.email} ${person.title}`,
    person,
  }));

  const update = (patch: Partial<DemoAccessState>) =>
    onChange({ ...state, ...patch });

  const addPerson = (person: DemoPerson) =>
    update({ grants: [...state.grants, { person, role: 'viewer' }] });

  const setRole = (personId: string, role: AccessRole) =>
    update({
      grants: state.grants.map((grant) =>
        grant.person.id === personId ? { ...grant, role } : grant,
      ),
    });

  const removePerson = (personId: string) => {
    const grants = state.grants.filter((grant) => grant.person.id !== personId);
    // nobody left to notify: drop the notification with the last recipient
    update({ grants, notify: grants.length > 0 && state.notify });
  };

  const toggleNotify = (notify: boolean) =>
    update({
      notify,
      // seed the note the first time it opens; a message the user already typed is kept
      message:
        notify && !state.message
          ? buildDefaultMessage(bundleTitle, workspaceName, t)
          : state.message,
    });

  const recipients = state.grants.length;

  return (
    <section className={styles.accessSection} aria-labelledby={`${ids}-title`}>
      <div className={styles.accessHeader}>
        <span id={`${ids}-title`} className={styles.itemsTitle}>
          {t('Share access')}
        </span>
      </div>

      <div className={styles.workspaceCard}>
        <span className={styles.workspaceAvatar} aria-hidden="true">
          {initials(workspaceName)}
        </span>
        <div className={styles.workspaceText}>
          <span className={styles.workspaceName}>{workspaceName}</span>
          <span className={styles.workspaceMeta}>
            {t('{{count}} members', { count: DEMO_PEOPLE.length + 1 })} ·{' '}
            {t('Only people you add will see this bundle')}
          </span>
        </div>
      </div>

      <div className={styles.personPicker}>
        <label htmlFor={`${ids}-people`}>{t('Add people')}</label>
        <Select
          inputId={`${ids}-people`}
          className="wf-select-container"
          classNamePrefix="wf-select"
          placeholder={t('Search people in {{workspace}}', {
            workspace: workspaceName,
          })}
          options={personOptions}
          value={null}
          onChange={(selected) =>
            selected && addPerson((selected as PersonOptionShape).person)
          }
          components={personSelectComponents}
          noOptionsMessage={() =>
            personOptions.length === 0
              ? t('Everyone in the workspace is added')
              : t('No people found')
          }
          isDisabled={disabled}
          isSearchable
          openMenuOnFocus
          {...selectPortalProps}
        />
      </div>

      <ul
        className={styles.accessList}
        aria-label={t('People with access') as string}
      >
        <li className={styles.accessRow}>
          <Avatar name={ownerName} color="var(--clr-gray600)" />
          <span className={styles.personText}>
            <span className={styles.personName}>
              {ownerName} <span className={styles.personYou}>({t('you')})</span>
            </span>
            <span className={styles.personMeta}>{ownerEmail}</span>
          </span>
          <span className={styles.ownerRole}>{t('Owner')}</span>
        </li>
        {state.grants.map(({ person, role }) => (
          <li key={person.id} className={styles.accessRow}>
            <Avatar name={person.name} color={person.color} />
            <span className={styles.personText}>
              <span className={styles.personName}>{person.name}</span>
              <span className={styles.personMeta}>
                {person.title} · {person.email}
              </span>
            </span>
            <div className={styles.roleSelect}>
              <Select
                aria-label={
                  t('Role for {{name}}', { name: person.name }) as string
                }
                className="wf-select-container"
                classNamePrefix="wf-select"
                options={roleOptions}
                value={
                  roleOptions.find((option) => option.value === role) ?? null
                }
                onChange={(selected) =>
                  selected &&
                  setRole(person.id, (selected as { value: AccessRole }).value)
                }
                formatOptionLabel={(option, { context }) =>
                  context === 'menu' ? (
                    <span className={styles.roleOption}>
                      <span>{option.label}</span>
                      <span className={styles.personMeta}>{option.hint}</span>
                    </span>
                  ) : (
                    option.label
                  )
                }
                components={singleSelectComponents}
                isSearchable={false}
                isDisabled={disabled}
                {...roleSelectPortalProps}
              />
            </div>
            <button
              type="button"
              className={styles.accessRemove}
              onClick={() => removePerson(person.id)}
              disabled={disabled}
              aria-label={t('Remove {{name}}', { name: person.name }) as string}
              title={t('Remove') as string}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {recipients === 0 && (
        <p className={styles.itemsHint}>
          {t(
            'Nobody else has access yet. Add teammates above to share the bundle.',
          )}
        </p>
      )}

      {/* native input rather than the shared Checkbox: that one hides its input with
          visibility: hidden, so it can't be reached from the keyboard */}
      <label className={styles.notifyField}>
        <input
          type="checkbox"
          className={styles.notifyInput}
          checked={state.notify}
          onChange={(event) => toggleNotify(event.target.checked)}
          disabled={disabled || recipients === 0}
        />
        <span>{t('Notify people by email about their new access')}</span>
      </label>

      {state.notify && (
        <div className={`form-element ${styles.messageField}`}>
          <label htmlFor={`${ids}-message`}>{t('Message')}</label>
          <textarea
            id={`${ids}-message`}
            className={styles.messageInput}
            rows={3}
            maxLength={MESSAGE_MAX_LENGTH}
            value={state.message}
            onChange={(event) => update({ message: event.target.value })}
            disabled={disabled}
            aria-describedby={`${ids}-message-hint`}
          />
          <div id={`${ids}-message-hint`} className={styles.messageFooter}>
            <span className={styles.messageRecipients}>
              <span className={styles.avatarStack} aria-hidden="true">
                {state.grants.slice(0, 4).map(({ person }) => (
                  <Avatar
                    key={person.id}
                    name={person.name}
                    color={person.color}
                    className={styles.avatarSmall}
                  />
                ))}
              </span>
              {t('Will be sent to {{count}} people', { count: recipients })}
              {' · '}
              {t('from {{name}}', { name: ownerName })}
            </span>
            <span className={styles.messageCounter}>
              {state.message.length}/{MESSAGE_MAX_LENGTH}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
